import { faPlain, toman } from 'utilities/helper/format'
import { CASHBOX_ROLE_LABELS } from 'utilities/helper/cashBoxes'
import { SectionError } from 'components/states'
import DotsLoading from 'components/loadings/dotsLoading'
import { useCashboxMembers, useShareGroupView } from '../hooks'

interface Props {
    id?: string
    cashbox: ICashboxDetail
    search: string
}

export default function MembersTab({ id, cashbox, search }: Props) {
    const { data, isLoading, isError, refetch } = useCashboxMembers(id, search)
    const { wholeShareMembers, groupViews } = useShareGroupView(
        data?.memberships,
        data?.share_groups,
        cashbox.period?.share_value ?? 0
    )

    if (isLoading) return <DotsLoading />
    if (isError)
        return (
            <div className="p-4">
                <SectionError onRetry={() => refetch()} />
            </div>
        )

    const isOwner = cashbox.membership?.role === 'own'

    return (
        <div className="px-4 py-3">
            {groupViews.length > 0 && (
                <>
                    <p className="text-sm font-bold text-black mb-2">
                        گروه‌های خرده‌سهم
                    </p>
                    {groupViews.map((view) => (
                        <div
                            key={view.group.id}
                            className="rounded-card border border-divider bg-white mb-3 overflow-hidden"
                        >
                            <div className="flex items-center justify-between px-3 py-2.5 bg-[#FAFAFA]">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-black truncate">
                                        {view.group.name || 'گروه بدون نام'}
                                    </p>
                                    <p className="text-xs text-text-secondary mt-0.5">
                                        {faPlain.format(view.members.length)}{' '}
                                        نفر · مجموع {toman(view.total)}
                                    </p>
                                </div>
                                {view.group.is_won && (
                                    <span className="text-xs font-bold text-[#4CA632] shrink-0">
                                        برنده شده
                                    </span>
                                )}
                            </div>

                            {/* The one state that silently breaks a box: a group
                                whose residues no longer add up to one share.
                                `Period.total_shares` keeps counting it, so
                                `satisfactory_amount` demands money nobody owes
                                and the draw can never fire. Say it out loud. */}
                            {!view.isBalanced && (
                                <div className="px-3 py-2 bg-[#FDECEA] border-t border-[#F5C2BD]">
                                    <p className="text-xs text-[#B3261E] leading-5">
                                        مجموع سهم اعضای این گروه{' '}
                                        {toman(view.total)} است، اما ارزش هر سهم{' '}
                                        {toman(cashbox.period?.share_value ?? 0)}{' '}
                                        است.
                                        {isOwner
                                            ? ' تا اصلاح این گروه، قرعه‌کشی صندوق انجام نمی‌شود.'
                                            : ' لطفاً به مدیر صندوق اطلاع دهید.'}
                                    </p>
                                </div>
                            )}

                            {view.members.length === 0 ? (
                                <p className="px-3 py-3 text-xs text-text-secondary">
                                    این گروه عضوی ندارد.
                                </p>
                            ) : (
                                view.members.map((m) => (
                                    <MemberRow
                                        key={m.id}
                                        member={m}
                                        isOwner={isOwner}
                                        amountLabel="سهم در گروه"
                                        amount={m.share_residue}
                                    />
                                ))
                            )}
                        </div>
                    ))}
                </>
            )}

            <p className="text-sm font-bold text-black mb-2">اعضا</p>
            {wholeShareMembers.length === 0 ? (
                <p className="text-sm text-text-secondary text-center py-6">
                    عضوی یافت نشد
                </p>
            ) : (
                <div className="rounded-card border border-divider bg-white overflow-hidden">
                    {wholeShareMembers.map((m) => (
                        <MemberRow
                            key={m.id}
                            member={m}
                            isOwner={isOwner}
                            amountLabel="باقی‌مانده"
                            amount={m.remaining_share_amount}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function MemberRow({
    member,
    isOwner,
    amountLabel,
    amount,
}: {
    member: ICashboxMembership
    isOwner: boolean
    amountLabel: string
    amount: number
}) {
    const isSettled = member.remaining_share_amount <= 0
    return (
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-divider last:border-b-0">
            <div className="min-w-0">
                <p className="text-sm text-black truncate">
                    {member.name?.trim() ||
                        member.phone_number ||
                        'عضو بدون نام'}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                    {member.role !== 'nrm' &&
                        `${CASHBOX_ROLE_LABELS[member.role]} · `}
                    {member.number_of_shares > 0
                        ? `${faPlain.format(member.number_of_shares)} سهم`
                        : 'خرده‌سهم'}
                    {member.non_won_shares === 0 &&
                        member.number_of_shares > 0 &&
                        ' · برنده شده'}
                    {isOwner && member.phone_number
                        ? ` · ${member.phone_number}`
                        : ''}
                </p>
            </div>
            <div className="text-left shrink-0 pl-1">
                <p className="text-xs text-text-secondary">{amountLabel}</p>
                <p
                    className={`text-sm font-bold ${
                        isSettled ? 'text-[#4CA632]' : 'text-black'
                    }`}
                >
                    {isSettled && amountLabel === 'باقی‌مانده'
                        ? 'تسویه'
                        : toman(amount)}
                </p>
            </div>
        </div>
    )
}
